/**
 * Database Client - Web versie (IndexedDB)
 * Regelt de verbinding met de browser database (fancy hoor)
 */

import Dexie, { Table } from 'dexie';
import { ProductDatabaseRow } from '../models';
import { DatabaseService } from './DatabaseService';

// Database configuration
const DATABASE_NAME = 'nebulatide_db';
const DATABASE_VERSION = 1;

// Dexie database klasse (wrapper om IndexedDB)
class NebulaTideDatabase extends Dexie {
  products!: Table<ProductDatabaseRow, number>;

  constructor() {
    super(DATABASE_NAME);

    this.version(DATABASE_VERSION).stores({
      // Schema definieren met indexes
      // Primary key is Nummer (auto-increment in originele DB)
      // Indexes op Barcode, Product (naam), en Merk voor snelheid (vroem vroem)
      products: 'Nummer, Barcode, Product, Merk, Categorie',
    });
  }
}

class DatabaseClientWeb implements DatabaseService {
  private db: NebulaTideDatabase;
  private initialized = false;

  constructor() {
    this.db = new NebulaTideDatabase();
  }

  /**
   * Database verbinding starten
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    try {
      // Database openen
      await this.db.open();

      // Checken of er data in zit
      const count = await this.db.products.count();

      if (count === 0) {
        console.warn('Database is leeg. Moet gevuld worden.');
        // Noot: Taak 4 regelt het vullen
      }

      this.initialized = true;
      console.log('IndexedDB succesvol gestart');
    } catch (error) {
      console.error('Kon IndexedDB niet starten:', error);
      throw new Error(
        `Kon database niet starten: ${error instanceof Error ? error.message : 'Onbekende fout'}`
      );
    }
  }

  /**
   * Is de database al klaar?
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Zoek op ID (Nummer)
   */
  async getProductById(id: number): Promise<ProductDatabaseRow | null> {
    try {
      if (!this.initialized) {
        throw new Error('Database niet gestart. Roep eerst initialize() aan.');
      }

      const product = await this.db.products.get(id);
      return product || null;
    } catch (error) {
      console.error(`Fout bij ophalen product op ID ${id}:`, error);
      throw new Error(
        `Kon product niet ophalen op ID: ${error instanceof Error ? error.message : 'Onbekende fout'}`
      );
    }
  }

  /**
   * Zoek op barcode
   */
  async getProductByBarcode(barcode: string): Promise<ProductDatabaseRow | null> {
    try {
      if (!this.initialized) {
        throw new Error('Database niet gestart. Roep eerst initialize() aan.');
      }

      // Gebruik de Barcode index voor snelheid
      const product = await this.db.products
        .where('Barcode')
        .equals(barcode)
        .first();

      return product || null;
    } catch (error) {
      console.error(`Fout bij ophalen product op barcode ${barcode}:`, error);
      throw new Error(
        `Kon product niet ophalen op barcode: ${error instanceof Error ? error.message : 'Onbekende fout'}`
      );
    }
  }

  /**
   * Zoeken op naam of merk
   */
  async searchProducts(searchTerm: string): Promise<ProductDatabaseRow[]> {
    try {
      if (!this.initialized) {
        throw new Error('Database not initialized. Call initialize() first.');
      }

      const searchLower = searchTerm.toLowerCase();

      // Zoek in Product (naam) en Merk (brand) velden
      // IndexedDB snapt geen LIKE queries, dus we filteren in geheugen
      const products = await this.db.products
        .filter(product => {
          const productName = product.Product?.toLowerCase() || '';
          const brandName = product.Merk?.toLowerCase() || '';
          return productName.includes(searchLower) || brandName.includes(searchLower);
        })
        .limit(50)
        .toArray();

      // Sorteren op productnaam (ABC)
      return products.sort((a, b) =>
        (a.Product || '').localeCompare(b.Product || '')
      );
    } catch (error) {
      console.error(`Error searching products with term "${searchTerm}":`, error);
      throw new Error(
        `Failed to search products: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Alles van een categorie ophalen
   */
  async getProductsByCategory(category: string): Promise<ProductDatabaseRow[]> {
    try {
      if (!this.initialized) {
        throw new Error('Database not initialized. Call initialize() first.');
      }

      // Categorie index gebruiken
      const products = await this.db.products
        .where('Categorie')
        .equals(category)
        .toArray();

      // Sorteren op naam
      return products.sort((a, b) =>
        (a.Product || '').localeCompare(b.Product || '')
      );
    } catch (error) {
      console.error(`Error getting products by category "${category}":`, error);
      throw new Error(
        `Failed to get products by category: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Willekeurige producten ophalen (voor discovery)
   */
  async getRandomProducts(limit: number): Promise<ProductDatabaseRow[]> {
    try {
      if (!this.initialized) {
        throw new Error('Database not initialized. Call initialize() first.');
      }

      const count = await this.db.products.count();
      if (count === 0) return [];

      const result: ProductDatabaseRow[] = [];
      const seenIds = new Set<number>();

      // Veiligheidje tegen oneindige loops
      const actualLimit = Math.min(limit, count);

      // Simpele inefficiënte manier voor kleine DB:
      // Random ID's pakken en gokken dat het goed gaat
      // (Beter dan niks toch?) 

      for (let i = 0; i < actualLimit; i++) {
        const randomOffset = Math.floor(Math.random() * count);
        const item = await this.db.products.offset(randomOffset).first();
        if (item) {
          // Dubbele voorkomen (al is de kans klein)
          if (!seenIds.has(item.Nummer)) {
            result.push(item);
            seenIds.add(item.Nummer);
          } else {
            i--; // Opnieuw proberen
          }
        }
      }

      return result;
    } catch (error) {
      console.error(`Error getting random products (limit ${limit}):`, error);
      // Fallback to empty array
      return [];
    }
  }

  /**
   * Bulk toevoegen (voor initiële data loading)
   * Dit wordt gebruikt door de data loader in taak 4
   */
  async bulkInsertProducts(products: ProductDatabaseRow[]): Promise<void> {
    try {
      if (!this.initialized) {
        throw new Error('Database not initialized. Call initialize() first.');
      }

      await this.db.products.bulkAdd(products);
      console.log(`Successfully inserted ${products.length} products`);
    } catch (error) {
      console.error('Error bulk inserting products:', error);
      throw new Error(
        `Failed to bulk insert products: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Alles wissen (voor reset)
   */
  async clearProducts(): Promise<void> {
    try {
      if (!this.initialized) {
        throw new Error('Database not initialized. Call initialize() first.');
      }

      await this.db.products.clear();
      console.log('All products cleared from database');
    } catch (error) {
      console.error('Error clearing products:', error);
      throw new Error(
        `Failed to clear products: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Hoeveel producten hebben we?
   */
  async getProductCount(): Promise<number> {
    try {
      if (!this.initialized) {
        throw new Error('Database not initialized. Call initialize() first.');
      }

      return await this.db.products.count();
    } catch (error) {
      console.error('Error getting product count:', error);
      throw new Error(
        `Failed to get product count: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }
}

// Export singleton instance
export const databaseClient = new DatabaseClientWeb();
